import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Activity, CheckCircle, XCircle, RefreshCw, Database, Wifi, WifiOff } from 'lucide-react';
import { APIs } from '../config/apis';
import { BACKEND_CONFIG } from '../config/backend';

const ApiStatus = ({ apiStatus, statusLoading, checkApiStatus }) => {
  const [detailedStatus, setDetailedStatus] = useState({});
  const [loadingDetailed, setLoadingDetailed] = useState(false);

  // Verificar status detalhado das APIs
  const checkDetailedStatus = async () => {
    setLoadingDetailed(true);
    try {
      const response = await fetch(`${BACKEND_CONFIG.BASE_URL}/api/proxy/health`);
      if (response.ok) {
        const data = await response.json();
        setDetailedStatus(data.apis || {});
      }
    } catch (error) {
      console.error('Erro ao verificar status detalhado:', error);
    } finally {
      setLoadingDetailed(false);
    }
  };

  useEffect(() => {
    checkDetailedStatus();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'offline':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Activity className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'bg-green-100 border-green-300 text-green-800';
      case 'offline':
        return 'bg-red-100 border-red-300 text-red-800';
      default:
        return 'bg-yellow-100 border-yellow-300 text-yellow-800';
    }
  };

  // Função para obter informações da API
  const getApiInfo = (apiKey) => {
    const apiInfo = {
      'apiBancoBitrix': {
        url: 'http://157.90.117.119',
        endpoint: '/json',
        description: 'API Principal - Dados de cobranças'
      },
      'apiRegistroInteresse': {
        url: 'http://91.99.68.90:3001',
        endpoint: '/json',
        description: 'Registro de Interesses'
      },
      'apiBitrixComentarios': {
        url: 'http://91.99.68.90:3003',
        endpoint: '/registroInteresse',
        description: 'Sistema de Comentários'
      },
      'apiBitrixComentarioNovo': {
        url: 'http://91.99.68.90:3003',
        endpoint: '/registroInteresse',
        description: 'Sistema Atualizado de Comentários'
      },
      'apiRegistroConversa': {
        url: 'http://91.99.68.90:3004',
        endpoint: '/registroInteresse',
        description: 'Sistema de Conversas'
      }
    };
    
    return apiInfo[apiKey] || {
      url: 'N/A',
      endpoint: 'N/A',
      description: 'API não configurada'
    };
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mb-8"
    >
      <div className="card">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Status das APIs</h2>
                <p className="text-xs text-gray-500">Monitoramento em tempo real</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={checkApiStatus}
                disabled={statusLoading}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm"
              >
                {statusLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span>Atualizar</span>
              </button>
              <button
                onClick={checkDetailedStatus}
                disabled={loadingDetailed}
                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
              >
                {loadingDetailed ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Database className="w-4 h-4" />
                )}
                <span>Detalhado</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Resumo geral */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <div className="text-center">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                📊 Resumo do Sistema
              </h3>
              <div className="flex items-center justify-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700">
                    {Object.values(detailedStatus).filter(api => api.status === 'online').length} Online
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-gray-700">
                    {Object.values(detailedStatus).filter(api => api.status === 'offline').length} Offline
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-700">
                    {Object.values(detailedStatus).filter(api => api.status === 'unknown').length} Desconhecido
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Status individual das APIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(APIs).map(([apiKey, api]) => {
              const IconComponent = api.icon;
              const apiStatusData = detailedStatus[apiKey] || {};
              const isOnline = apiStatusData.status === 'online';
              const isOffline = apiStatusData.status === 'offline';
              const isUnknown = !apiStatusData.status;
              const apiInfo = getApiInfo(apiKey);
              
              return (
                <motion.div
                  key={apiKey}
                  whileHover={{ y: -2 }}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                    isOnline 
                      ? 'bg-green-50 border-green-300 shadow-lg' 
                      : isOffline 
                        ? 'bg-red-50 border-red-300 shadow-lg' 
                        : 'bg-yellow-50 border-yellow-300 shadow-lg'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${api.color}`}>
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{api.name}</h4>
                        <p className="text-xs text-gray-600">{apiInfo.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(apiStatusData.status)}
                      <span className={`text-xs font-medium px-2 py-1 rounded-full border ${
                        isOnline ? 'bg-green-100 text-green-800 border-green-300' :
                        isOffline ? 'bg-red-100 text-red-800 border-red-300' :
                        'bg-yellow-100 text-yellow-800 border-yellow-300'
                      }`}>
                        {isOnline ? 'Online' : isOffline ? 'Offline' : 'Desconhecido'}
                      </span>
                    </div>
                  </div>

                  {/* Detalhes do status */}
                  <div className="space-y-2 text-xs">
                    {isOnline && apiStatusData.responseTime && (
                      <div className="flex items-center justify-between text-green-700">
                        <span>⏱️ Tempo de resposta:</span>
                        <span className="font-medium">{apiStatusData.responseTime}ms</span>
                      </div>
                    )}
                    
                    {isOnline && apiStatusData.statusCode && (
                      <div className="flex items-center justify-between text-green-700">
                        <span>📡 Status HTTP:</span>
                        <span className="font-medium">{apiStatusData.statusCode}</span>
                      </div>
                    )}
                    
                    {isOffline && apiStatusData.error && (
                      <div className="text-red-700">
                        <span>❌ Erro:</span>
                        <span className="font-medium ml-1">{apiStatusData.error}</span>
                      </div>
                    )}
                    
                    {isOffline && apiStatusData.errorCode && (
                      <div className="text-red-700">
                        <span>🔧 Código:</span>
                        <span className="font-medium ml-1">{apiStatusData.errorCode}</span>
                      </div>
                    )}
                  </div>

                  {/* Informações específicas */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex items-center justify-between">
                        <span>🔗 Endpoint:</span>
                        <span className="font-mono">{apiInfo.url}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>📂 Rota:</span>
                        <span className="font-mono">{apiInfo.endpoint}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Informações adicionais */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">ℹ️ Informações das APIs</h4>
            <div className="text-xs text-gray-600 space-y-1">
              <p>• <strong>API Banco Bitrix:</strong> Funcionando normalmente - dados principais do sistema</p>
              <p>• <strong>API Registro Interesse:</strong> Usa endpoint <code>/json</code> para listar e buscar arquivos</p>
              <p>• <strong>API Comentários:</strong> Usa endpoint <code>/registroInteresse</code> para gerenciar dados</p>
              <p>• <strong>API Comentários Novo:</strong> Sistema atualizado com mesmas rotas</p>
              <p>• <strong>API Registro Conversa:</strong> Usa endpoint <code>/registroInteresse</code> para conversas</p>
              <p>• <strong>Proxy:</strong> Todas as consultas passam pelo backend para segurança</p>
              <p>• <strong>Status:</strong> Atualizado automaticamente a cada verificação</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ApiStatus;
